import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Radio } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { floatEmojis } from "@/lib/celebrate";
import { UserAvatar } from "@/components/UserBits";

export const sideLabel = (d, s) => (d.spectator ? s : s === d.i_am ? "me" : "other");
const sideUser = (d, s) => (d.spectator ? (s === "a" ? d.me : d.other) : s === d.i_am ? d.me : d.other);

// floats every newly arrived cheer above the cheered player's card
export function useCheerFx(d) {
  const seen = useRef(null);
  useEffect(() => {
    if (!d?.cheers) return;
    if (seen.current === null) { seen.current = new Set(d.cheers.map((c) => c.id)); return; }
    d.cheers.filter((c) => !seen.current.has(c.id)).reverse().forEach((c, i) => {
      seen.current.add(c.id);
      setTimeout(() => floatEmojis(document.querySelector(`[data-testid="vault-duel-side-${sideLabel(d, c.side)}"]`), c.emoji, 4), i * 120);
    });
  }, [d]);
}

export function CheerBar({ d, reload }) {
  const [side, setSide] = useState("a");
  const send = async (emoji, e) => {
    floatEmojis(e.currentTarget, emoji, 5);
    try { await api.post(`/vault-duel/${d.id}/cheer`, { emoji, side }); reload(); }
    catch (err) { toast.error(formatErr(err)); }
  };
  if (d.status !== "live") return null;
  return (
    <div className="rounded-2xl bg-white/5 p-3 space-y-2" data-testid="vault-duel-cheerbar">
      <div className="flex items-center justify-between text-xs font-bold text-white/60">
        <span>Anfeuern für</span><span data-testid="vault-duel-cheers-left">noch {d.cheers_left}×</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {["a", "b"].map((s) => (
          <button key={s} onClick={() => setSide(s)} data-testid={`vault-duel-cheer-side-${s}`} className={`h-9 rounded-full text-sm font-extrabold truncate px-3 transition-colors ${side === s ? "bg-rose-500 text-white" : "bg-white/10 text-white/70"}`}>
            {sideUser(d, s).user?.name?.split(" ")[0] || "Spieler"} · {d.cheer_counts[s]}
          </button>
        ))}
      </div>
      <div className="flex justify-between gap-1">
        {d.emojis.map((em) => (
          <button key={em} onClick={(e) => send(em, e)} disabled={d.cheers_left <= 0} data-testid={`vault-duel-cheer-${em}`} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-xl active:scale-90 transition-transform disabled:opacity-30">{em}</button>
        ))}
      </div>
    </div>
  );
}

export function LiveDuels() {
  const [list, setList] = useState([]);
  useEffect(() => {
    const load = () => api.get("/vault-duel-live").then(({ data }) => setList(data)).catch(() => {});
    load();
    const iv = setInterval(() => document.visibilityState === "visible" && load(), 10000);
    return () => clearInterval(iv);
  }, []);
  if (!list.length) return null;
  return (
    <div className="space-y-1.5" data-testid="vault-live-duels">
      <p className="text-xs font-extrabold uppercase tracking-widest text-rose-300 flex items-center gap-1.5"><Radio className="w-4 h-4 animate-pulse" /> Jetzt live</p>
      {list.map((d) => (
        <Link key={d.id} to={`/spiele/tresor-duell/${d.id}`} data-testid={`vault-live-${d.id}`} className="flex items-center gap-2 rounded-2xl bg-rose-500/15 hover:bg-rose-500/25 px-3 py-2 transition-colors">
          <UserAvatar userId={d.me.user?.user_id} name={d.me.user?.name} picture={d.me.user?.picture} size="w-8 h-8" text="text-xs" />
          <span className="text-xs font-black text-rose-300">VS</span>
          <UserAvatar userId={d.other.user?.user_id} name={d.other.user?.name} picture={d.other.user?.picture} size="w-8 h-8" text="text-xs" />
          <span className="min-w-0 flex-1 text-sm font-bold truncate">{d.me.user?.name?.split(" ")[0]} vs. {d.other.user?.name?.split(" ")[0]} · {d.stake} P.</span>
          <span className="inline-flex items-center gap-1 text-xs font-extrabold text-amber-300"><Eye className="w-4 h-4" /> Zuschauen</span>
        </Link>
      ))}
    </div>
  );
}
